package com.wintermar.inspection.feature.presentation.inspection.answer

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.wintermar.inspection.core.data.Uuid
import com.wintermar.inspection.core.ui.component.image.util.FileChunkModel
import com.wintermar.inspection.core.ui.component.image.util.FileModel
import com.wintermar.inspection.core.ui.component.image.util.FileUploadStatus
import com.wintermar.inspection.core.ui.component.location.util.LocationModel
import com.wintermar.inspection.core.ui.util.SelectionModel
import com.wintermar.inspection.feature.domain.model.answer.AnswerFileModel
import com.wintermar.inspection.feature.domain.model.answer.AnswerModel
import com.wintermar.inspection.feature.domain.model.auth.UserModel
import com.wintermar.inspection.feature.domain.model.template.InspectionTemplateModel
import com.wintermar.inspection.feature.domain.model.template.InspectionTemplateQuestionModel
import com.wintermar.inspection.feature.domain.model.template.TemplateOptionModel
import com.wintermar.inspection.feature.presentation.inspection.answer.modal_state.ModalDateState
import com.wintermar.inspection.feature.presentation.inspection.answer.modal_state.ModalFindingCategory
import com.wintermar.inspection.feature.presentation.inspection.answer.modal_state.ModalGuidanceState
import com.wintermar.inspection.feature.presentation.inspection.answer.modal_state.ModalLocationState
import com.wintermar.inspection.feature.presentation.inspection.answer.modal_state.ModalMediaState
import com.wintermar.inspection.feature.presentation.inspection.answer.modal_state.ModalNotesState
import com.wintermar.inspection.graphql.type.FindingCategory
import com.wintermar.inspection.graphql.type.InspectionAnswerType
import com.wintermar.inspection.util.Constant

abstract class AnswerState(
    private val inspectionId: String,
    private val onSaveAnswer: (
        answer: AnswerModel,
        answerType: InspectionAnswerType,
        includeUploadFile: Boolean
    ) -> Unit,
    private val onReattachUploadCallback: () -> Unit
) {
    open var template by mutableStateOf(
        InspectionTemplateModel()
    )
    var isOfflineMode by mutableStateOf(false)
    var isLoading by mutableStateOf(false)
    var answerOptions by mutableStateOf(listOf<TemplateOptionModel>())
    var vessels by mutableStateOf(listOf<SelectionModel>())
    var modalNotes by mutableStateOf(ModalNotesState())
    var modalFindingCategory by mutableStateOf(ModalFindingCategory())
    var modalFiles by mutableStateOf(ModalMediaState())
    var modalDate by mutableStateOf(ModalDateState())
    var modalLocation by mutableStateOf(ModalLocationState())
    var modalPageInfo by mutableStateOf(false)
    var modalGuidance by mutableStateOf(ModalGuidanceState())
    var user by mutableStateOf(UserModel())
    val findingCategoryOptions = listOf(
        FindingCategory.MAJOR_NC,
        FindingCategory.MINOR_NC,
        FindingCategory.OBSERVATION
    )
    val isInternalAudit: Boolean
        get() = template.name.contains(Constant.INTERNAL_AUDIT, ignoreCase = true)

    private fun handleSaveAnswer(questionId: String, includeUploadFile: Boolean = false) {
        template.pages
            .flatMap { it.sections }
            .flatMap { it.questions }
            .find { it.id == questionId }
            ?.let {
                onSaveAnswer(
                    it.answer,
                    it.answerType,
                    includeUploadFile
                )
            }
    }

    private fun updateTemplateQuestions(
        questionId: String,
        skipSaveAnswer: Boolean = false,
        includeUploadFile: Boolean = false,
        predicate: (InspectionTemplateQuestionModel) -> InspectionTemplateQuestionModel,
    ) {
        template = template.copy(
            pages = template.pages.map { page ->
                page.copy(
                    sections = page.sections.map { section ->
                        section.copy(
                            questions = section.questions.map(predicate).map { question ->
                                question.copy(
                                    answer = question.answer.copy(
                                        id = question.answer.id.ifEmpty { Uuid.generate() },
                                        inspectionId = question.answer.inspectionId.ifEmpty { inspectionId },
                                        questionId = question.answer.questionId.ifEmpty { question.id }
                                    )
                                )
                            }
                        )
                    }
                )
            }
        )
        if (!skipSaveAnswer) {
            handleSaveAnswer(questionId, includeUploadFile)
        }
    }

    fun setAnswerOption(questionId: String, answerOptionId: String) {
        updateTemplateQuestions(questionId) { question ->
            if (question.id == questionId) {
                val isFailed = answerOptionId == question.failedOptionId
                question.copy(
                    answer = question.answer.copy(
                        answerOptionId = answerOptionId,
                        isRequiredFileOrNotes = isFailed,
                        findingCategory = when {
                            isFailed && !isInternalAudit -> FindingCategory.OBSERVATION
                            isFailed -> question.answer.findingCategory
                            else -> null
                        }
                    ),
                )
            } else question
        }
    }

    fun setAnswerVessel(questionId: String, vesselId: String = "", vesselName: String = "") {
        updateTemplateQuestions(questionId) { question ->
            if (question.id == questionId) {
                question.copy(
                    answer = question.answer.copy(
                        answerSelection = question.answer.answerSelection.copy(
                            id = question.answer.answerSelection.id.ifEmpty { Uuid.generate() },
                            answerSelectionId = vesselId,
                            answerSelectionValue = vesselName
                        )
                    ),
                )
            } else question
        }
    }

    fun setAnswerTextChanged(questionId: String, text: String) {
        updateTemplateQuestions(questionId) { question ->
            if (question.id == questionId) {
                question.copy(
                    answer = question.answer.copy(
                        answerText = text
                    ),
                )
            } else question
        }
    }

    fun setDataTemplate(template: InspectionTemplateModel) {
        var userQuestionId = ""
        this.template = template.copy(
            pages = template.pages.map { page ->
                page.copy(
                    sections = page.sections.map { section ->
                        section.copy(
                            questions = section.questions.map { question ->
                                val isUserQuestion =
                                    question.answerType == InspectionAnswerType.USER_ID
                                val questionId =
                                    if (isUserQuestion && question.id.isEmpty()) Uuid.generate() else question.id
                                question.setOptions(answerOptions).copy(
                                    id = questionId,
                                    answer = question.answer.copy(
                                        inspectionId = inspectionId,
                                        questionId = questionId,
                                        id = question.answer.id.ifEmpty { Uuid.generate() },
                                        answerSelection =
                                            if (isUserQuestion) {
                                                userQuestionId = questionId
                                                question.answer.answerSelection.copy(
                                                    id = question.answer.answerSelection.id.ifEmpty { Uuid.generate() },
                                                    answerSelectionId = user.userId,
                                                    answerSelectionValue = user.fullName
                                                )
                                            } else question.answer.answerSelection
                                    )
                                )
                            }
                        )
                    }
                )
            }
        )

        if (userQuestionId.isNotEmpty()) {
            handleSaveAnswer(userQuestionId)
        }
    }

    fun onShowModalNotes(show: Boolean, questionId: String = "", notes: String = "") {
        modalNotes = modalNotes.copy(
            show = show,
            questionId = questionId,
            notes = notes
        )
    }

    fun onShowModalFindingCategory(
        show: Boolean,
        questionId: String = "",
        findingCategory: FindingCategory? = null
    ) {
        modalFindingCategory = modalFindingCategory.copy(
            show = show,
            questionId = questionId,
            findingCategory = findingCategory
        )
    }

    fun onShowModalMedia(
        show: Boolean,
        questionId: String = "",
        files: List<AnswerFileModel> = emptyList()
    ) {
        modalFiles = modalFiles.copy(
            show = show,
            questionId = questionId,
            files = files.map { file ->
                FileModel(
                    id = file.id,
                    path = file.path,
                    name = file.name,
                    size = file.size,
                    type = file.type,
                    originalName = file.originalName,
                    uploadStatus = file.uploadStatus,
                    currentUpload = file.currentUpload,
                    blob = file.blob
                )
            }
        )
    }

    fun onShowModalDate(
        show: Boolean,
        title: String = "",
        questionId: String = "",
        dateIso: String = ""
    ) {
        modalDate = modalDate.copy(
            show = show,
            title = title,
            questionId = questionId,
            dateIso = dateIso
        )
    }

    fun onShowModalLocation(show: Boolean, questionId: String = "", name: String = "") {
        modalLocation = modalLocation.copy(
            show = show,
            questionId = questionId,
            name = name
        )
    }

    fun onSaveNotes(questionId: String, notes: String) {
        updateTemplateQuestions(questionId) { question ->
            if (question.id == questionId) {
                question.copy(
                    answer = question.answer.copy(
                        notes = notes
                    )
                )
            } else question
        }
    }

    fun onSaveFindingCategory(questionId: String, findingCategory: FindingCategory?) {
        updateTemplateQuestions(questionId) { question ->
            if (question.id == questionId) {
                question.copy(
                    answer = question.answer.copy(
                        findingCategory = findingCategory
                    )
                )
            } else question
        }
    }

    fun onSaveFiles(questionId: String, files: List<FileModel>) {
        updateTemplateQuestions(
            questionId,
            skipSaveAnswer = false,
            includeUploadFile = true
        ) { question ->
            if (question.id == questionId) {
                question.copy(
                    answer = question.answer.copy(
                        files = files.map { file ->
                            AnswerFileModel(
                                id = file.id,
                                answerId = question.answer.id,
                                path = file.path,
                                name = file.name,
                                originalName = file.originalName,
                                size = file.size,
                                type = file.type,
                                blob = file.blob,
                                chunkFiles = file.chunkFiles,
                                uploadStatus = FileUploadStatus.WAITING_TO_UPLOAD,
                                currentUpload = file.currentUpload,
                                platformFile = file.platformFile,
                                hasGenerateChunk = file.hasGenerateChunk
                            )
                        }
                    )
                )
            } else question
        }
    }

    fun updateUploadFile(
        questionId: String,
        fileId: String,
        uploadStatus: FileUploadStatus,
        currentUpload: FileChunkModel? = null
    ) {
        updateTemplateQuestions(questionId, true) { question ->
            if (question.id == questionId) {
                question.copy(
                    answer = question.answer.copy(
                        files = question.answer.files.map { file ->
                            if (file.id == fileId) {
                                file.copy(
                                    uploadStatus = uploadStatus,
                                    currentUpload = currentUpload
                                )
                            } else file
                        }
                    )
                )
            } else question
        }
    }

    fun updateHasGeneratedChunk(
        questionId: String,
        fileIds: List<String>
    ) {
        updateTemplateQuestions(questionId, true) { question ->
            if (question.id == questionId) {
                question.copy(
                    answer = question.answer.copy(
                        files = question.answer.files.map { file ->
                            if (fileIds.contains(file.id)) {
                                file.copy(
                                    hasGenerateChunk = true
                                )
                            } else file
                        }
                    )
                )
            } else question
        }
    }

    fun onSaveModalFiles(files: List<FileModel>) {
        onSaveFiles(questionId = modalFiles.questionId, files = files)
        onShowModalMedia(show = false)
    }

    fun onSaveModalNotes(notes: String) {
        onSaveNotes(questionId = modalNotes.questionId, notes = notes)
        onShowModalNotes(show = false)
    }

    fun onSaveModalFindingCategory(findingCategory: FindingCategory?) {
        onSaveFindingCategory(
            questionId = modalFindingCategory.questionId,
            findingCategory = findingCategory
        )
        onShowModalFindingCategory(show = false)
    }

    fun onSaveModalLocation(name: String, location: LocationModel?) {
        updateTemplateQuestions(modalLocation.questionId) { question ->
            if (question.id == modalLocation.questionId) {
                question.copy(
                    answer = question.answer.copy(
                        answerLocation = question.answer.answerLocation.copy(
                            id = question.answer.answerLocation.id.ifEmpty { Uuid.generate() },
                            answerId = question.answer.id,
                            name = name,
                            latitude = location?.latitude ?: 0.0,
                            longitude = location?.longitude ?: 0.0,
                            street = location?.street ?: "",
                            isoCountryCode = location?.isoCountryCode ?: "",
                            country = location?.country ?: "",
                            postalCode = location?.postalCode ?: "",
                            administrativeArea = location?.administrativeArea ?: "",
                            subAdministrativeArea = location?.subAdministrativeArea ?: "",
                            locality = location?.locality ?: "",
                            subLocality = location?.subLocality ?: "",
                            thoroughfare = location?.thoroughfare ?: "",
                            subThoroughfare = location?.subThoroughfare ?: ""
                        )
                    )
                )
            } else question
        }
        onShowModalLocation(show = false)
    }

    fun onSaveModalDate(dateIso: String) {
        updateTemplateQuestions(modalDate.questionId) { question ->
            if (question.id == modalDate.questionId) {
                question.copy(
                    answer = question.answer.copy(
                        answerDateIso = dateIso
                    )
                )
            } else question
        }
        onShowModalDate(show = false)
    }

    fun onShowModalPageInfo(show: Boolean) {
        modalPageInfo = show
    }

    fun onShowModalGuidance(show: Boolean, question: InspectionTemplateQuestionModel? = null) {
        modalGuidance = modalGuidance.copy(
            show = show,
            question = question
        )
    }

    fun onReattachUploadCallbackListener() {
        onReattachUploadCallback()
    }
}
